import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { LoadingOutlined } from '@ant-design/icons'
import { Button, DatePicker, Drawer, Flex, Form, InputNumber, Modal, Select, Space, Spin, Switch } from 'antd'
import { format, parse } from 'date-fns'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import styles from './HolidaysTab.module.scss'
import viewStyles from './ViewStudio.module.scss'
import { AppConstants } from '../../../../AppContants'
import Loading from '../../../../components/Loading/Loading'
import addSvg from '../../../../images/add.svg'
import deleteSvg from '../../../../images/delete.svg'
import Arrow from '../../../../images/dropArrow.svg'
import {
  deleteClinicHolidayAsync,
  getClinicHolidaysAsync,
  IClinicHoliday,
  IClinicHolidayHours,
  IClinicHolidayHoursInput,
  ICreateClinicHoliday,
  IUpdateClinicHoliday,
  postClinicHolidayAsync,
  putClinicHolidayAsync,
  resetDeleteHolidayStatus,
  resetHolidays,
  resetHolidaysStatus,
  resetPostHolidayStatus,
  resetPutHolidayStatus,
} from '../../../../redux/admin/studio'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { StatusEnum } from '../../../../redux/type'
import { notifyError, notifySuccess } from '../../../../utils/Notification'

const normalizeHolidayTimeValue = (time?: string) => {
  if (!time) {
    return undefined
  }
  return time.slice(0, 5)
}

const parse24hToHolidayFormTime = (time?: string) => {
  if (!time) {
    return { time: '8:00', meridiem: 'AM' }
  }
  const normalized = time.length <= 5 ? `${normalizeHolidayTimeValue(time)}:00` : time
  const parsed = new Date(`2024-01-01T${normalized}`)
  return {
    time: format(parsed, 'h:mm'),
    meridiem: format(parsed, 'a').toUpperCase(),
  }
}

const holidayFormTimeTo24h = (time: string, meridiem: string) => {
  const parsed = parse(`${time} ${meridiem}`, 'h:mm a', new Date())
  return format(parsed, 'HH:mm')
}

const formatHolidayTime12h = (time: string) => {
  const { time: timeLabel, meridiem } = parse24hToHolidayFormTime(time)
  return `${timeLabel} ${meridiem}`
}

const formatHolidayDateDisplay = (dateStr: string) => {
  const parsed = parse(dateStr, 'yyyy-MM-dd', new Date())
  return format(parsed, 'EEE, MMM d, yyyy')
}

/** Open windows of a holiday, earliest first. No windows means closed all day. */
const getHolidayWindows = (holiday: IClinicHoliday): Array<IClinicHolidayHours> => {
  if (!holiday.hours) {
    return []
  }
  return [...holiday.hours].sort((a, b) => a.openTime.localeCompare(b.openTime))
}

const getHolidayWindowUi = (window: IClinicHolidayHours) => ({
  primary: `${formatHolidayTime12h(window.openTime)} – ${formatHolidayTime12h(window.closeTime)}`,
  noOfTables: window.noOfTables ?? null,
  frequency: window.frequency ?? null,
})

/** Scan / new-lead permissions belong to the date, so every window shares them. */
const getHolidayDayToggles = (holiday: IClinicHoliday) => {
  const windows = getHolidayWindows(holiday)
  if (windows.length === 0) {
    return { allowScan: null as boolean | null, allowNewLeads: null as boolean | null }
  }
  return {
    allowScan: holiday.allowScan ?? windows[0].allowScan ?? true,
    allowNewLeads: holiday.allowNewLeads ?? windows[0].allowNewLeads ?? true,
  }
}

interface HolidayWindowFormValue {
  openTime?: string
  openMeridiem?: string
  closeTime?: string
  closeMeridiem?: string
  noOfTables?: number
  frequency?: number
}

interface HolidayFormValues {
  date: dayjs.Dayjs
  isPartialHours: boolean
  windows?: Array<HolidayWindowFormValue>
  allowScan?: boolean
  allowNewLeads?: boolean
}

const PARTIAL_HOUR_DEFAULTS = {
  openTime: '8:00',
  openMeridiem: 'AM',
  closeTime: '5:00',
  closeMeridiem: 'PM',
  noOfTables: 1,
  frequency: 1,
}

const DAY_TOGGLE_DEFAULTS = {
  allowScan: true,
  allowNewLeads: true,
}

const getWindowFormValues = (window: HolidayWindowFormValue) => ({
  openTime: window.openTime ?? PARTIAL_HOUR_DEFAULTS.openTime,
  openMeridiem: window.openMeridiem ?? PARTIAL_HOUR_DEFAULTS.openMeridiem,
  closeTime: window.closeTime ?? PARTIAL_HOUR_DEFAULTS.closeTime,
  closeMeridiem: window.closeMeridiem ?? PARTIAL_HOUR_DEFAULTS.closeMeridiem,
  noOfTables: window.noOfTables ?? PARTIAL_HOUR_DEFAULTS.noOfTables,
  frequency: window.frequency ?? PARTIAL_HOUR_DEFAULTS.frequency,
})

/** Form windows as API payload, earliest first. Empty means closed all day. */
const buildHolidayHoursPayload = (values: HolidayFormValues): Array<IClinicHolidayHoursInput> => {
  if (!values.isPartialHours) {
    return []
  }
  return (values.windows ?? [])
    .map((window) => {
      const formValues = getWindowFormValues(window)
      return {
        openTime: holidayFormTimeTo24h(formValues.openTime, formValues.openMeridiem),
        closeTime: holidayFormTimeTo24h(formValues.closeTime, formValues.closeMeridiem),
        noOfTables: formValues.noOfTables,
        frequency: formValues.frequency,
      }
    })
    .sort((a, b) => a.openTime.localeCompare(b.openTime))
}

/** Same rules the API enforces, checked here so the drawer stays open on failure. */
const getHolidayHoursError = (hours: Array<IClinicHolidayHoursInput>) => {
  if (hours.some((window) => window.openTime >= window.closeTime)) {
    return 'Start time must be before end time'
  }
  for (let index = 1; index < hours.length; index++) {
    if (hours[index].openTime < hours[index - 1].closeTime) {
      return 'Hours must not overlap each other'
    }
  }
  return null
}

const getHolidayFormWindows = (holiday: IClinicHoliday): Array<HolidayWindowFormValue> => {
  const windows = getHolidayWindows(holiday)
  if (windows.length === 0) {
    return [{ ...PARTIAL_HOUR_DEFAULTS }]
  }
  return windows.map((window) => {
    const open = parse24hToHolidayFormTime(window.openTime)
    const close = parse24hToHolidayFormTime(window.closeTime)
    return {
      openTime: open.time,
      openMeridiem: open.meridiem,
      closeTime: close.time,
      closeMeridiem: close.meridiem,
      noOfTables: window.noOfTables ?? PARTIAL_HOUR_DEFAULTS.noOfTables,
      frequency: window.frequency ?? PARTIAL_HOUR_DEFAULTS.frequency,
    }
  })
}

const buildCreateHolidayPayload = (values: HolidayFormValues): ICreateClinicHoliday => ({
  date: values.date.format('YYYY-MM-DD'),
  hours: buildHolidayHoursPayload(values),
  allowScan: values.allowScan ?? DAY_TOGGLE_DEFAULTS.allowScan,
  allowNewLeads: values.allowNewLeads ?? DAY_TOGGLE_DEFAULTS.allowNewLeads,
})

const buildUpdateHolidayPayload = (holiday: IClinicHoliday, values: HolidayFormValues): IUpdateClinicHoliday => {
  const payload: IUpdateClinicHoliday = {
    hours: buildHolidayHoursPayload(values),
    allowScan: values.allowScan ?? DAY_TOGGLE_DEFAULTS.allowScan,
    allowNewLeads: values.allowNewLeads ?? DAY_TOGGLE_DEFAULTS.allowNewLeads,
  }
  const nextDate = values.date.format('YYYY-MM-DD')
  if (nextDate !== holiday.date) {
    payload.date = nextDate
  }
  return payload
}

const holidayTimeSelectProps = {
  options: AppConstants.TimeSelectorFifteenInterval,
  suffixIcon: <img src={Arrow} alt="arrowDown" style={{ marginTop: '0.3rem' }} />,
  className: styles.holidayTimeSelect,
  style: { textTransform: 'capitalize' as const },
  dropdownStyle: { textTransform: 'capitalize' as const, border: 'solid 1px #2c2d3c' },
}

const holidayMeridiemSelectProps = {
  options: AppConstants.Meridiem,
  suffixIcon: <img src={Arrow} alt="arrowDown" style={{ marginTop: '0.3rem' }} />,
  className: styles.holidayMeridiemSelect,
  style: { textTransform: 'capitalize' as const },
  dropdownStyle: { textTransform: 'capitalize' as const, border: 'solid 1px #2c2d3c' },
}

interface HolidayPartialHoursFieldsProps {
  isPartialHours: boolean
}

const HolidayPartialHoursFields = ({ isPartialHours }: HolidayPartialHoursFieldsProps) => (
  <div className={isPartialHours ? undefined : styles.holidayPartialHoursHidden}>
    <Form.List name="windows">
      {(fields, { add, remove }) => (
        <div className={styles.holidayScheduleLikeSection}>
          {fields.map(({ key, name }, index) => (
            <div key={key} className={styles.holidayWindowCard}>
              <div className={styles.holidayWindowHeader}>
                <span className={styles.holidayWindowTitle}>Hours {index + 1}</span>
                {fields.length > 1 && <img src={deleteSvg} alt="Remove hours" onClick={() => remove(name)} />}
              </div>

              <div className={styles.holidayTimeColumns}>
                <Form.Item label="Start" required={isPartialHours} className={styles.holidayTimeField}>
                  <div className={styles.holidayTimeRow}>
                    <div className={styles.holidayTimeSelectItem}>
                      <Form.Item
                        name={[name, 'openTime']}
                        rules={isPartialHours ? [{ required: true, message: 'Please select open time' }] : []}
                        noStyle
                      >
                        <Select placeholder="Start Time" disabled={!isPartialHours} {...holidayTimeSelectProps} />
                      </Form.Item>
                    </div>
                    <div className={styles.holidayMeridiemSelectItem}>
                      <Form.Item
                        name={[name, 'openMeridiem']}
                        rules={isPartialHours ? [{ required: true, message: 'Please select AM/PM' }] : []}
                        noStyle
                      >
                        <Select placeholder="AM/PM" disabled={!isPartialHours} {...holidayMeridiemSelectProps} />
                      </Form.Item>
                    </div>
                  </div>
                </Form.Item>

                <Form.Item label="End" required={isPartialHours} className={styles.holidayTimeField}>
                  <div className={styles.holidayTimeRow}>
                    <div className={styles.holidayTimeSelectItem}>
                      <Form.Item
                        name={[name, 'closeTime']}
                        rules={isPartialHours ? [{ required: true, message: 'Please select close time' }] : []}
                        noStyle
                      >
                        <Select placeholder="End Time" disabled={!isPartialHours} {...holidayTimeSelectProps} />
                      </Form.Item>
                    </div>
                    <div className={styles.holidayMeridiemSelectItem}>
                      <Form.Item
                        name={[name, 'closeMeridiem']}
                        rules={isPartialHours ? [{ required: true, message: 'Please select AM/PM' }] : []}
                        noStyle
                      >
                        <Select placeholder="AM/PM" disabled={!isPartialHours} {...holidayMeridiemSelectProps} />
                      </Form.Item>
                    </div>
                  </div>
                </Form.Item>
              </div>

              <div className={styles.holidayMetaRow}>
                <Form.Item
                  label="No. of tables"
                  name={[name, 'noOfTables']}
                  rules={isPartialHours ? [{ required: true, message: 'Please enter number of tables' }] : []}
                  className={styles.holidayMetaField}
                >
                  <InputNumber
                    disabled={!isPartialHours}
                    min={1}
                    precision={0}
                    controls={false}
                    className={styles.holidayNumberInput}
                  />
                </Form.Item>

                <Form.Item
                  label="Frequency"
                  name={[name, 'frequency']}
                  rules={isPartialHours ? [{ required: true, message: 'Please enter frequency' }] : []}
                  className={styles.holidayMetaField}
                >
                  <InputNumber
                    disabled={!isPartialHours}
                    min={1}
                    precision={0}
                    controls={false}
                    className={styles.holidayNumberInput}
                  />
                </Form.Item>
              </div>
            </div>
          ))}

          <div className={styles.holidayAddWindowRow}>
            <img
              className={styles.holidayAddWindowIcon}
              src={addSvg}
              alt="Add hours"
              onClick={() => isPartialHours && add({ ...PARTIAL_HOUR_DEFAULTS })}
            />
            <span onClick={() => isPartialHours && add({ ...PARTIAL_HOUR_DEFAULTS })}>Add another set of hours</span>
          </div>
        </div>
      )}
    </Form.List>

    {/* Applies to the whole date, so it sits outside the per-set fields. */}
    <div className={styles.holidayDayToggleSection}>
      <div className={styles.holidayToggleRow}>
        <Form.Item<HolidayFormValues>
          label="Allow scan"
          name="allowScan"
          valuePropName="checked"
          className={styles.holidayToggleField}
        >
          <Switch disabled={!isPartialHours} />
        </Form.Item>

        <Form.Item<HolidayFormValues>
          label="Allow new leads"
          name="allowNewLeads"
          valuePropName="checked"
          className={styles.holidayToggleField}
        >
          <Switch disabled={!isPartialHours} />
        </Form.Item>
      </div>
    </div>
  </div>
)

interface HolidayDeleteConfirmModalProps {
  showModal: boolean
  title: string
  isLoading: boolean
  onConfirm: () => void
  closeModal: () => void
}

const HolidayDeleteConfirmModal = ({
  showModal,
  title,
  isLoading,
  onConfirm,
  closeModal,
}: HolidayDeleteConfirmModalProps) => {
  return (
    <Modal
      open={showModal}
      centered
      closable={false}
      mask
      maskClosable={!isLoading}
      footer={null}
      onCancel={closeModal}
    >
      <div className={styles.confirmModal}>
        <div className={styles.confirmModalTitle}>{title}</div>
        <Flex gap={12} justify="center" className={styles.confirmModalActions}>
          <Button className={styles.confirmModalCancel} disabled={isLoading} onClick={closeModal}>
            No
          </Button>
          <Button className={styles.confirmModalConfirm} disabled={isLoading} onClick={onConfirm}>
            {isLoading ? <Spin indicator={<LoadingOutlined style={{ color: '#fffeff' }} spin />} /> : 'Yes'}
          </Button>
        </Flex>
      </div>
    </Modal>
  )
}

interface HolidayFormProps {
  onClose: () => void
}

const AddHoliday = ({ onClose }: HolidayFormProps) => {
  const { id } = useParams()
  const dispatch = useAppDispatch()
  const postHolidayStatus = useAppSelector((state) => state.admin.studio.postHolidayStatus)
  const [form] = Form.useForm<HolidayFormValues>()

  const onFinish = (values: HolidayFormValues) => {
    const payload = buildCreateHolidayPayload(values)
    const hoursError = getHolidayHoursError(payload.hours ?? [])
    if (hoursError) {
      notifyError('Invalid holiday hours', hoursError)
      return
    }
    dispatch(postClinicHolidayAsync({ studioId: id, data: payload }))
  }

  const onCancel = () => {
    form.resetFields()
    onClose()
  }

  const isPartialHours = Form.useWatch('isPartialHours', form)

  return (
    <div className={`${viewStyles.wrapper} ${styles.holidayForm}`}>
      <Form
        form={form}
        layout="vertical"
        className={styles.holidayFormFields}
        initialValues={{
          isPartialHours: false,
          windows: [{ ...PARTIAL_HOUR_DEFAULTS }],
          ...DAY_TOGGLE_DEFAULTS,
        }}
        onFinish={onFinish}
        requiredMark={false}
      >
        <Form.Item<HolidayFormValues>
          label="Date"
          name="date"
          rules={[{ required: true, message: 'Please select a date' }]}
        >
          <DatePicker
            className={styles.holidayFormDatePicker}
            disabledDate={(current) => !!current && current.endOf('day').isBefore(dayjs().startOf('day'))}
          />
        </Form.Item>

        <Form.Item<HolidayFormValues> label="Partial hours" name="isPartialHours" valuePropName="checked">
          <Switch />
        </Form.Item>

        <HolidayPartialHoursFields isPartialHours={!!isPartialHours} />

        <Form.Item className={styles.holidayFormActions}>
          <div className={viewStyles.footer}>
            <Button className={viewStyles.button} type="default" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              className={viewStyles.button}
              htmlType="submit"
              type="primary"
              loading={postHolidayStatus === StatusEnum.Pending}
            >
              Submit
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  )
}

interface EditHolidayProps extends HolidayFormProps {
  holiday: IClinicHoliday
}

const EditHoliday = ({ holiday, onClose }: EditHolidayProps) => {
  const { id } = useParams()
  const dispatch = useAppDispatch()
  const putHolidayStatus = useAppSelector((state) => state.admin.studio.putHolidayStatus)
  const [form] = Form.useForm<HolidayFormValues>()

  useEffect(() => {
    const dayToggles = getHolidayDayToggles(holiday)
    form.setFieldsValue({
      date: dayjs(holiday.date),
      isPartialHours: getHolidayWindows(holiday).length > 0,
      windows: getHolidayFormWindows(holiday),
      allowScan: dayToggles.allowScan ?? DAY_TOGGLE_DEFAULTS.allowScan,
      allowNewLeads: dayToggles.allowNewLeads ?? DAY_TOGGLE_DEFAULTS.allowNewLeads,
    })
  }, [form, holiday])

  const onFinish = (values: HolidayFormValues) => {
    const payload = buildUpdateHolidayPayload(holiday, values)
    const hoursError = getHolidayHoursError(payload.hours ?? [])
    if (hoursError) {
      notifyError('Invalid holiday hours', hoursError)
      return
    }
    dispatch(
      putClinicHolidayAsync({
        studioId: id,
        holidayId: holiday.id,
        data: payload,
      }),
    )
  }

  const onCancel = () => {
    form.resetFields()
    onClose()
  }

  const isPartialHours = Form.useWatch('isPartialHours', form)

  return (
    <div className={`${viewStyles.wrapper} ${styles.holidayForm}`}>
      <Form
        form={form}
        layout="vertical"
        className={styles.holidayFormFields}
        initialValues={{
          isPartialHours: false,
          windows: [{ ...PARTIAL_HOUR_DEFAULTS }],
          ...DAY_TOGGLE_DEFAULTS,
        }}
        onFinish={onFinish}
        requiredMark={false}
      >
        <Form.Item<HolidayFormValues>
          label="Date"
          name="date"
          rules={[{ required: true, message: 'Please select a date' }]}
        >
          <DatePicker
            className={styles.holidayFormDatePicker}
            disabledDate={(current) => !!current && current.endOf('day').isBefore(dayjs().startOf('day'))}
          />
        </Form.Item>

        <Form.Item<HolidayFormValues> label="Partial hours" name="isPartialHours" valuePropName="checked">
          <Switch />
        </Form.Item>

        <HolidayPartialHoursFields isPartialHours={!!isPartialHours} />

        <Form.Item className={styles.holidayFormActions}>
          <div className={viewStyles.footer}>
            <Button className={viewStyles.button} type="default" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              className={viewStyles.button}
              htmlType="submit"
              type="primary"
              loading={putHolidayStatus === StatusEnum.Pending}
            >
              Save
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  )
}

export const HolidayTab = () => {
  const dispatch = useAppDispatch()
  const { id } = useParams()
  const holidays = useAppSelector((state) => state.admin.studio.holidays)
  const holidaysStatus = useAppSelector((state) => state.admin.studio.holidaysStatus)
  const postHolidayStatus = useAppSelector((state) => state.admin.studio.postHolidayStatus)
  const putHolidayStatus = useAppSelector((state) => state.admin.studio.putHolidayStatus)
  const deleteHolidayStatus = useAppSelector((state) => state.admin.studio.deleteHolidayStatus)
  const apiError = useAppSelector((state) => state.admin.studio.error)
  const [open, setOpen] = useState(false)
  const [showEditHoliday, setShowEditHoliday] = useState(false)
  const [selectedHoliday, setSelectedHoliday] = useState<IClinicHoliday | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [holidayToDelete, setHolidayToDelete] = useState<IClinicHoliday | null>(null)

  const refetchHolidays = useCallback(() => {
    dispatch(getClinicHolidaysAsync({ studioId: id }))
  }, [dispatch, id])

  useEffect(() => {
    refetchHolidays()
    return () => {
      dispatch(resetHolidaysStatus())
      dispatch(resetHolidays())
    }
  }, [refetchHolidays, dispatch])

  useEffect(() => {
    if (holidaysStatus === StatusEnum.Failed) {
      notifyError('Unable to fetch holidays', apiError)
    }
  }, [holidaysStatus])

  useEffect(() => {
    if (postHolidayStatus === StatusEnum.Failed) {
      notifyError('Unable to create holiday', apiError)
    } else if (postHolidayStatus === StatusEnum.Success) {
      notifySuccess('Success', 'Holiday created successfully')
      setOpen(false)
      refetchHolidays()
    }
    return () => {
      dispatch(resetPostHolidayStatus())
    }
  }, [postHolidayStatus])

  useEffect(() => {
    if (putHolidayStatus === StatusEnum.Failed) {
      notifyError('Unable to update holiday', apiError)
    } else if (putHolidayStatus === StatusEnum.Success) {
      notifySuccess('Success', 'Holiday updated successfully')
      setShowEditHoliday(false)
      setSelectedHoliday(null)
      refetchHolidays()
    }
    return () => {
      dispatch(resetPutHolidayStatus())
    }
  }, [putHolidayStatus])

  useEffect(() => {
    if (deleteHolidayStatus === StatusEnum.Failed) {
      notifyError('Unable to delete holiday', apiError)
    } else if (deleteHolidayStatus === StatusEnum.Success) {
      notifySuccess('Success', 'Holiday deleted successfully')
      setShowDeleteModal(false)
      setHolidayToDelete(null)
      refetchHolidays()
    }
    return () => {
      dispatch(resetDeleteHolidayStatus())
    }
  }, [deleteHolidayStatus])

  const sortedHolidays = useMemo(() => holidays, [holidays])

  const holidayRowCount = useMemo(
    () => holidays.reduce((total, holiday) => total + Math.max(getHolidayWindows(holiday).length, 1), 0),
    [holidays],
  )

  const closeAddHolidayDrawer = () => setOpen(false)
  const closeEditHolidayDrawer = () => {
    setShowEditHoliday(false)
    setSelectedHoliday(null)
  }

  const handleDeleteConfirm = () => {
    if (!holidayToDelete) {
      return
    }
    dispatch(deleteClinicHolidayAsync({ studioId: id, holidayId: holidayToDelete.id }))
  }

  const isDeleteLoading = deleteHolidayStatus === StatusEnum.Pending

  return (
    <div>
      <div className={styles.holidayTabPanel} style={{ height: holidayRowCount > 20 ? '35vh' : '41vh' }}>
        {holidaysStatus === StatusEnum.Pending && <Loading style={{ background: 'none' }}></Loading>}

        {holidaysStatus === StatusEnum.Success && holidays.length === 0 && (
          <div className={styles.holidaysEmptyContainer}>
            <Button type="primary" onClick={() => setOpen(true)}>
              Add Holiday
            </Button>
          </div>
        )}

        {holidaysStatus === StatusEnum.Success && holidays.length > 0 && (
          <div className={styles.holidayListContainer}>
            <div className={styles.holidayListHeader}>
              <Button type="primary" onClick={() => setOpen(true)}>
                Add Holiday
              </Button>
            </div>

            <div className={styles.holidayListTable}>
              <div className={styles.holidayListColumnHeader}>
                <div className={styles.holidayDateCol}>Date</div>
                <div className={styles.holidayHoursCol}>Hours</div>
                <div className={styles.holidayMetricCol}>Tables</div>
                <div className={styles.holidayMetricCol}>Frequency</div>
                <div className={styles.holidayToggleCol}>Scan</div>
                <div className={styles.holidayToggleCol}>New Leads</div>
                <div className={styles.holidayActionCol}></div>
              </div>

              {sortedHolidays.map((holiday) => {
                const windows = getHolidayWindows(holiday)
                const openEditHoliday = () => {
                  setSelectedHoliday(holiday)
                  setShowEditHoliday(true)
                }
                const deleteIcon = (
                  <img
                    src={deleteSvg}
                    alt="Delete holiday"
                    onClick={(event) => {
                      event.stopPropagation()
                      setHolidayToDelete(holiday)
                      setShowDeleteModal(true)
                    }}
                  />
                )

                if (windows.length === 0) {
                  return (
                    <div key={holiday.id} className={styles.holidayListRow} onClick={openEditHoliday}>
                      <div className={styles.holidayDateCol}>{formatHolidayDateDisplay(holiday.date)}</div>
                      <div className={styles.holidayHoursCol}>Closed full day</div>
                      <div className={styles.holidayMetricCol}>-</div>
                      <div className={styles.holidayMetricCol}>-</div>
                      <div className={styles.holidayToggleCol}>-</div>
                      <div className={styles.holidayToggleCol}>-</div>
                      <div className={styles.holidayActionCol}>{deleteIcon}</div>
                    </div>
                  )
                }

                // Scan / new leads apply to the whole date, so they show once,
                // on the first row, alongside the date itself.
                const dayToggles = getHolidayDayToggles(holiday)
                return (
                  <Fragment key={holiday.id}>
                    {windows.map((window, index) => {
                      const hoursUi = getHolidayWindowUi(window)
                      const isFirstWindow = index === 0
                      const isLastWindow = index === windows.length - 1
                      return (
                        <div
                          key={window.id}
                          className={`${styles.holidayListRow} ${isLastWindow ? '' : styles.holidayListRowGrouped}`}
                          onClick={openEditHoliday}
                        >
                          <div className={styles.holidayDateCol}>
                            {isFirstWindow ? formatHolidayDateDisplay(holiday.date) : ''}
                          </div>
                          <div className={styles.holidayHoursCol}>{hoursUi.primary}</div>
                          <div className={styles.holidayMetricCol}>{hoursUi.noOfTables ?? '-'}</div>
                          <div className={styles.holidayMetricCol}>{hoursUi.frequency ?? '-'}</div>
                          <div className={styles.holidayToggleCol}>
                            {isFirstWindow ? (dayToggles.allowScan ? 'Allowed' : 'Not Allowed') : ''}
                          </div>
                          <div className={styles.holidayToggleCol}>
                            {isFirstWindow ? (dayToggles.allowNewLeads ? 'Allowed' : 'Not Allowed') : ''}
                          </div>
                          <div className={styles.holidayActionCol}>{isFirstWindow ? deleteIcon : null}</div>
                        </div>
                      )
                    })}
                  </Fragment>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <Drawer
        title="Add Holiday"
        closable={false}
        width={400}
        classNames={{ body: styles.holidayDrawerBody }}
        extra={
          <Space>
            <div onClick={closeAddHolidayDrawer} className={viewStyles.closeContainer}></div>
          </Space>
        }
        open={open}
      >
        {open && <AddHoliday onClose={closeAddHolidayDrawer}></AddHoliday>}
      </Drawer>

      <Drawer
        title="Edit Holiday"
        closable={false}
        width={400}
        classNames={{ body: styles.holidayDrawerBody }}
        extra={
          <Space>
            <div onClick={closeEditHolidayDrawer} className={viewStyles.closeContainer}></div>
          </Space>
        }
        open={showEditHoliday}
      >
        {showEditHoliday && selectedHoliday && (
          <EditHoliday holiday={selectedHoliday} onClose={closeEditHolidayDrawer}></EditHoliday>
        )}
      </Drawer>

      <HolidayDeleteConfirmModal
        showModal={showDeleteModal}
        title="Delete this holiday?"
        isLoading={isDeleteLoading}
        onConfirm={handleDeleteConfirm}
        closeModal={() => {
          if (!isDeleteLoading) {
            setShowDeleteModal(false)
            setHolidayToDelete(null)
          }
        }}
      />
    </div>
  )
}
